import React, { useState, useEffect, useCallback } from 'react';
import Pagination from '../Pagination';
import axios from '../../api/api';
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';

const StkReport = () => {

  const [stkreport, setStkreport] = useState([]);
  const [searchPromocode, setSearchpromocode] = useState('')
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [count, setCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Convert a date to partition format pYYYYMMDD
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch StkReport reports
  const getStkReports = useCallback(async (offset, limit, promocode) => {
    try {
      let query = `?offset=${offset}&limit=${limit}`

      if (promocode) {
        query += `&promocode=${promocode}`
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 10) {
          alert('Date range cannot exceed 10 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);
        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      const response = await axios.get(`/reports/stk_push_archive${query}`);
      setStkreport(response.data.items);
      setCount(response.data.count)
      setTotal(response.data.total)
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error.response) {
        setError('Network error! Check your connection.');
      } else if (error.response.status >= 500) {
        setError('Server error! Please try again later.');
      } else {
        setError('An unexpected error occurred!');
      }
    }
  }, [endDate, startDate]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getStkReports((currentPage - 1) * recordsPerPage, recordsPerPage, searchPromocode);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, getStkReports, searchPromocode]);

  // search failed requests by msisdn
  const handleSearchChange = (e) => {
    setSearchpromocode(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className='mx-auto p-4'>
      <h3 className="my-2"><b>Sales Reports</b></h3>
      <div className="bg-white rounded-lg p-4 w-full flex-1 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 py-2"> 
          <form className='flex flex-wrap gap-2 items-center'>
            <label htmlFor='search'><span className='hidden'>Search</span>
              <input
                type='search'
                id='search'
                className='px-3 py-1 border bg-[#eef1ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                required
                placeholder='Search promocode...'
                value={searchPromocode}
                onChange={handleSearchChange}
              />
            </label>
            <label htmlFor="">
              <span className='hidden'>Start Date</span>
              <input 
                type="date"
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={startDate}
                onChange={(e) => {setStartDate(e.target.value)}}
              />
            </label>
            <label htmlFor="">
              <span className='hidden'>End Date</span>
              <input 
                type="date" 
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={endDate}
                onChange={(e) => {setEndDate(e.target.value)}}
              />
            </label>
          </form>
          <div>
            <span className='font-bold text-[#2b1e3e]'>Showing {count} / {total.toLocaleString() || 0} records</span>
          </div>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#eef1ff] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#eef1ff] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3 className='flex items-center justify-center'><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          <div className='overflow-x-auto rounded-lg'>
            {stkreport.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>count</th>
                      <th className='p-2'>sum (Total)</th>
                      <th className='p-2'>promocode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stkreport.map((stk) => (
                      <tr key={stk.id}>
                        <td className='p-2'>{stk.count}</td>
                        <td className='p-2'>{stk.sum}</td>
                        <td className='p-2'>{stk.promocode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#eef1ff] text-[#134463] grid place-items-center h-[70vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3 className='flex items-center justify-center'><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
        )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#eef1ff] border-slate-300 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='15'>15</option>
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default StkReport;
